import React, { useState, useEffect } from 'react';
import { Modal, Button, Table, ProgressBar, Form, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFileAlt, 
  faFileImage, 
  faFileVideo, 
  faFile,
  faDownload,
  faTrash,
  faPencilAlt
} from '@fortawesome/free-solid-svg-icons';
import { useFileManagerContext, SET_FILE_MANAGER_STATE, FileType } from '../../../providers/FileManager';
import FileSystemService from '../../../service/FileSystemService';

interface FileDetailsModalProps {
  onClose: () => void;
}

const FileDetailsModal: React.FC<FileDetailsModalProps> = ({ onClose }) => {
  const { fileManagerState, fileManagerDispatch } = useFileManagerContext();
  const { selectedFile } = fileManagerState;
  
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [newFileName, setNewFileName] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  
  // Initialize form state when modal opens
  useEffect(() => {
    if (selectedFile) {
      setNewFileName(selectedFile.name);
    }
  }, [selectedFile]);
  
  if (!selectedFile) return null;
  
  // Get file icon based on extension
  const getFileIcon = (extension?: string) => {
    switch (extension?.toLowerCase()) {
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return faFileImage;
      case 'mp4':
      case 'mov':
      case 'avi':
        return faFileVideo;
      case 'pdf':
      case 'doc':
      case 'docx':
      case 'txt':
        return faFileAlt;
      default:
        return faFile;
    }
  };

  // Format file size to readable format
  const formatFileSize = (size: number): string => {
    if (size < 1024) {
      return `${size} B`;
    } else if (size < 1024 * 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    } else if (size < 1024 * 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    } else {
      return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    }
  };

  // Format date
  const formatDate = (date?: Date): string => {
    if (!date) return 'Unknown';
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  
  // Handle download
  const handleDownload = (): void => {
    // In a real application, you would initiate a download here
    console.log(`Downloading file: ${selectedFile.path}`);
    // Alert for demo purposes
    alert(`Download functionality would be implemented here.\nFile: ${selectedFile.name}`);
  };
  
  // Handle delete
  const handleDelete = async (): Promise<void> => {
    if (!selectedFile) return;
    
    setIsDeleting(true);
    setError(null);
    
    try {
      await FileSystemService.deleteFile(selectedFile.path);
      
      // Close the modal
      onClose();
      
      // Refresh the file manager
      fileManagerDispatch({
        type: SET_FILE_MANAGER_STATE,
        payload: { refreshTimestamp: Date.now() }
      });
    } catch (err) {
      console.error('Error deleting file:', err);
      setError(`Failed to delete file: ${err instanceof Error ? err.message : String(err)}`);
      setIsDeleting(false);
    }
  };
  
  // Toggle rename mode
  const toggleRenameMode = (): void => {
    setIsRenaming(!isRenaming);
    if (!isRenaming) {
      setNewFileName(selectedFile.name);
    }
  };
  
  // Handle rename
  const handleRename = async (): Promise<void> => {
    if (!selectedFile || !newFileName.trim()) return;
    
    setError(null);
    
    try {
      await FileSystemService.renameFile(selectedFile.path, newFileName);
      
      // Exit rename mode
      setIsRenaming(false);
      
      // Close the modal
      onClose();
      
      // Refresh the file manager
      fileManagerDispatch({
        type: SET_FILE_MANAGER_STATE,
        payload: { refreshTimestamp: Date.now() }
      });
    } catch (err) {
      console.error('Error renaming file:', err);
      setError(`Failed to rename file: ${err instanceof Error ? err.message : String(err)}`);
    }
  };
  
  // Get file path parts
  const getPathParts = (): string[] => {
    if (!selectedFile?.path) return [];
    return selectedFile.path.split('/').filter(p => p);
  };
  
  // Render file preview
  const renderFilePreview = (): JSX.Element => {
    const extension = selectedFile.extension?.toLowerCase();
    
    if (extension === 'jpg' || extension === 'jpeg' || extension === 'png' || extension === 'gif') {
      return (
        <div className="text-center my-3" style={{ maxHeight: '200px', overflow: 'hidden' }}>
          <img 
            src="/api/placeholder/400/320" 
            alt={selectedFile.name}
            className="img-fluid" 
            style={{ maxHeight: '200px', objectFit: 'contain' }}
          />
        </div>
      );
    } else if (extension === 'mp4' || extension === 'mov' || extension === 'avi') {
      return (
        <div className="d-flex justify-content-center align-items-center bg-dark my-3" style={{ height: '200px' }}>
          <FontAwesomeIcon icon={faFileVideo} className="text-light" size="5x" />
        </div>
      );
    } else {
      return (
        <div className="d-flex justify-content-center align-items-center bg-light my-3" style={{ height: '150px' }}>
          <FontAwesomeIcon 
            icon={getFileIcon(extension || '')} 
            className="text-primary"
            size="5x"
          />
        </div>
      );
    }
  };

  return (
    <Modal show={true} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <div className="d-flex align-items-center">
            <FontAwesomeIcon 
              icon={getFileIcon(selectedFile.extension || '')} 
              className="text-primary me-2"
            />
            {isRenaming ? (
              <Form.Control
                type="text"
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                autoFocus
              />
            ) : (
              selectedFile.name
            )}
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {error && (
          <Alert variant="danger" onClose={() => setError(null)} dismissible>
            {error}
          </Alert>
        )}
        
        {/* File preview */}
        {renderFilePreview()}
        
        {/* File details */}
        <Table bordered>
          <tbody>
            <tr>
              <td className="fw-bold" style={{ width: '30%' }}>Location</td>
              <td>
                <span className="text-muted">
                  /{getPathParts().slice(0, -1).join('/')}
                </span>
              </td>
            </tr>
            <tr>
              <td className="fw-bold">Type</td>
              <td>{selectedFile.extension?.toUpperCase() || 'Unknown'} File</td>
            </tr>
            <tr>
              <td className="fw-bold">Size</td>
              <td>{formatFileSize(selectedFile.size || 0)}</td>
            </tr>
            <tr>
              <td className="fw-bold">Last Modified</td>
              <td>{formatDate(selectedFile.lastModified)}</td>
            </tr>
          </tbody>
        </Table>
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex w-100 justify-content-between">
          <div>
            <Button 
              variant="danger" 
              className="me-2"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              <FontAwesomeIcon icon={faTrash} className="me-1" />
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </div>
          
          <div>
            {isRenaming ? (
              <>
                <Button 
                  variant="secondary" 
                  className="me-2"
                  onClick={toggleRenameMode}
                >
                  Cancel
                </Button>
                <Button 
                  variant="success"
                  onClick={handleRename}
                  disabled={!newFileName.trim()}
                >
                  Save
                </Button>
              </>
            ) : (
              <>
                <Button 
                  variant="outline-primary" 
                  className="me-2"
                  onClick={toggleRenameMode}
                >
                  <FontAwesomeIcon icon={faPencilAlt} className="me-1" />
                  Rename
                </Button>
                <Button 
                  variant="primary"
                  onClick={handleDownload}
                >
                  <FontAwesomeIcon icon={faDownload} className="me-1" />
                  Download
                </Button>
              </>
            )}
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default FileDetailsModal;